import {
  BasicModal,
  Box,
  Icon,
  Menu,
  MenuItem,
  Typography,
  useTheme,
} from "@outschool/backpack";
import { statesHaveFundingProgram } from "@outschool/funding-programs-shared";
import { faArrowRight, faChevronDown } from "@outschool/icons";
import { useLookupIP } from "@outschool/iplookup-client";
import { useTranslation } from "@outschool/localization";
import {
  addParamsToUrl,
  onboardingQuizPath,
  searchPath,
} from "@outschool/routes";
import { IntegrationCategory, useTrackEvent } from "@outschool/ui-analytics";
import {
  AuthTrigger,
  SelfElectedStateFundingProgram,
  useLoginFlowContext,
  useSelfElectedStateFundingProgram,
  useSession,
} from "@outschool/ui-auth";
import { TrackedButton } from "@outschool/ui-components-shared";
import { useNavigation } from "@outschool/ui-utils";
import Cookies from "js-cookie";
import React from "react";
import { useLocation } from "react-router-dom";

import { US_STATES } from "../shared/publicFundingConstants";
import { StateFundingForm } from "./StateFundingOptions";

export default function SelfIdentificationPublicFunding() {
  const { t } = useTranslation(
    "client\\components\\banners\\SelfIdentificationPublicFunding",
  );
  const { currentUser, isEsaSession, esaSessionData } = useSession();
  const theme = useTheme();
  const navigate = useNavigation();
  const {
    hideBanner,
    selfElectedStateFundingProgram,
    setSelfElectedStateFundingProgram,
    setState,
    usesPublicFundingOrCharter,
    setFundingOptions,
    setSource,
    resetState,
  } = useSelfElectedStateFundingProgram();
  const { isInUSA } = useLookupIP();
  const { enterLoginFlow } = useLoginFlowContext();
  const location = useLocation();
  const [firstModalOpen, setFirstModalOpen] = React.useState(false);
  const [secondModalOpen, setSecondModalOpen] = React.useState(false);
  const [isBannerVisible, setIsBannerVisible] = React.useState(false);
  const trackAdEvent = useTrackEvent(IntegrationCategory.Advertising);

  // Show banner after 1000ms delay
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setIsBannerVisible(true);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  // If user is from CW, Set user ESA info from ESA Session
  React.useEffect(() => {
    const stateName = US_STATES.find(
      state => state.abbreviation === esaSessionData?.shipping?.state,
    )?.name;
    if (!currentUser && esaSessionData && stateName) {
      setSelfElectedStateFundingProgram({
        hasRejectedStateFundingQuestions: false,
        hideBanner: true,
        searchFiltersActive: true,
        stateAbbreviation: esaSessionData.shipping!.state,
        stateName,
        source: "classwallet",
        fundingOptions: {
          charter: false,
          statePortal: true,
          otherProgramName: "",
          reimbursement: false,
          selfPay: false,
          stateDebitCard: false,
          stateProgramName: "default",
        },
      });
    }
  }, [
    currentUser,
    isEsaSession,
    esaSessionData,
    setSelfElectedStateFundingProgram,
  ]);

  React.useEffect(() => {
    if (Cookies.get("hasUsedPublicFunding")) {
      trackAdEvent("signedUpWithPublicFundingOptions", {
        hasUsedPublicFunding: true,
      });
      Cookies.remove("hasUsedPublicFunding");
    }
  }, [trackAdEvent]);

  const handleContinueToSecondModal = () => {
    setFirstModalOpen(false);
    // timeout after .25s to open second modal
    setTimeout(() => {
      setSecondModalOpen(true);
    }, 250);
  };

  const handleCloseModals = () => {
    setFirstModalOpen(false);
    setSecondModalOpen(false);
    resetState();
    // refresh page to set filters
    navigate(location.pathname, { hardNav: true });
  };

  const handleNo = () => {
    hideBanner({ withRejection: true, resetState: true });
  };

  const handleYes = () => {
    setFirstModalOpen(true);
  };

  const handleCreateAccount = () => {
    if (!selfElectedStateFundingProgram.source) {
      setSource("banner");
    }
    setFirstModalOpen(false);
    setSecondModalOpen(false);
    hideBanner();

    const redirectUrlAfterLoginFlow = searchPath();
    if (usesPublicFundingOrCharter()) {
      // open up sign up flow
      enterLoginFlow({
        authTrigger: AuthTrigger.PUBLIC_FUNDING_BANNER,
        shouldNavigateOverwrite: false,
        newUrlAfterLogin: redirectUrlAfterLoginFlow,
        newUrlAfterSignUp: addParamsToUrl(onboardingQuizPath(), {
          landingPage: location.pathname + location.search,
        }),
      });
    } else {
      navigate(redirectUrlAfterLoginFlow, { hardNav: true });
    }
  };

  const handleSelfPayExit = () => {
    setFirstModalOpen(false);
    setSecondModalOpen(false);
    hideBanner({ withRejection: true, resetState: true });
  };

  const isOnboardingQuizPage = location.pathname === onboardingQuizPath();

  if (
    selfElectedStateFundingProgram.hideBanner ||
    !!currentUser ||
    !isInUSA ||
    !!isEsaSession ||
    isOnboardingQuizPage
  ) {
    return null;
  }

  return (
    <>
      <Box
        flex
        sx={{
          background:
            "linear-gradient(195deg, rgba(56, 5, 150, 0.00) 16.68%, #380596 87.95%), #4B01D4",
          color: "primary.contrastText",
          width: "100%",
          alignItems: "center",
          justifyContent: "center",
          paddingY: 2,
          paddingX: 3,
          textAlign: "center",
          position: "fixed",
          bottom: isBannerVisible ? 0 : -200,
          left: 0,
          zIndex: 1000,
          boxShadow: "0px -2px 8px rgba(0, 0, 0, 0.1)",
          minHeight: 80,
          transition: "bottom 0.5s ease-in-out",
          [theme.breakpoints.down("sm")]: {
            bottom: isBannerVisible ? 0 : -300,
          },
        }}
      >
        <Box
          flex
          sx={{
            flexDirection: "row",
            justifyContent: "center",
            alignItems: "center",
            gap: 10,
            padding: 10,
            [theme.breakpoints.down("sm")]: {
              flexDirection: "column",
              p: 10,
              gap: 5,
            },
          }}
        >
          <Typography
            variant="body1"
            sx={{
              fontWeight: 500,
              maxWidth: 900,
              textAlign: "left",
            }}
          >
            {t(
              "Are you planning to use state funding, such as an Education Savings Account (ESA), Micro-grant or scholarship to help pay for your learner’s education?",
            )}
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              gap: 10,
              alignSelf: "flex-start",
              [theme.breakpoints.down(1000)]: {
                width: "100%",
                flexDirection: "column",
                gap: 5,
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                gap: 10,
                alignSelf: "flex-start",
                [theme.breakpoints.down(1000)]: {
                  width: "100%",
                  flexDirection: "column",
                  gap: 5,
                },
              }}
            >
              <TrackedButton
                variant="contained"
                trackingName="public_funding_self_identification_banner_yes"
                trackViews={true}
                sx={{
                  borderColor: "white",
                  height: "32px",
                  borderWidth: 1,
                  width: 86,
                  [theme.breakpoints.down("sm")]: {
                    width: "100%",
                    marginY: 0,
                  },
                }}
                onClick={handleYes}
              >
                {t("Yes")}
              </TrackedButton>
              <TrackedButton
                variant="contained"
                trackingName="public_funding_self_identification_banner_no"
                sx={{
                  borderColor: "white",
                  height: "32px",
                  borderWidth: 1,
                  width: 86,
                  [theme.breakpoints.down("sm")]: {
                    width: "100%",
                    marginY: 0,
                  },
                }}
                onClick={handleNo}
              >
                {t("No")}
              </TrackedButton>
            </Box>
            <Box
              sx={{
                width: "1px",
                border: "1px solid white",
                borderWidth: "0 1px 0 0",
                height: 32,
                mx: 5,
                alignSelf: "flex-start",
                [theme.breakpoints.down(1000)]: {
                  display: "none",
                },
              }}
            />
            <TrackedButton
              variant="outlined"
              trackingName="public_funding_self_identification_banner_learn_more"
              sx={{
                borderColor: "white",
                height: "32px",
                display: "flex",
                alignItems: "center",
                alignSelf: "flex-start",
                "&:hover:not(:disabled)": {
                  borderColor: "transparent",
                },
                [theme.breakpoints.down(1000)]: {
                  width: "100%",
                  backgroundColor: "transparent",
                  borderColor: "transparent",
                  color: "white",
                },
              }}
              onClick={() => {
                navigate("/esa", {
                  hardNav: true,
                  newTab: true,
                });
              }}
            >
              {t("Learn more")}
              <Icon icon={faArrowRight} sx={{ marginLeft: 5 }} />
            </TrackedButton>
          </Box>
        </Box>
      </Box>
      <BasicModal
        hasCloseButton
        closeButtonArialLabel={t("Close")}
        open={firstModalOpen}
        onClose={handleCloseModals}
        sx={{
          [theme.breakpoints.down("sm")]: {
            width: "100%",
            margin: 0,
          },
          "& .MuiBox-root": {
            borderRadius: 15,
          },
        }}
        modalCloseButtonProps={{
          // eslint-disable-next-line i18next/no-literal-string
          size: "large",
          sx: {
            fontSize: 24,
          },
        }}
      >
        <SelectStateForm
          handleContinue={handleContinueToSecondModal}
          setState={setState}
          selfElectedStateFundingProgram={selfElectedStateFundingProgram}
        />
      </BasicModal>

      <BasicModal
        hasCloseButton
        closeButtonArialLabel={t("Close")}
        open={secondModalOpen}
        onClose={handleCloseModals}
        modalCloseButtonProps={{
          // eslint-disable-next-line i18next/no-literal-string
          size: "large",
          sx: {
            fontSize: 24,
          },
        }}
        sx={{
          "& .MuiBox-root": {
            borderRadius: 15,
          },
        }}
      >
        <StateFundingForm
          handleContinue={handleCreateAccount}
          handleSelfPayExit={handleSelfPayExit}
          setFundingOptions={setFundingOptions}
          selfElectedStateFundingProgram={selfElectedStateFundingProgram}
        />
      </BasicModal>
    </>
  );
}

export function SelectStateForm({
  handleContinue,
  setState,
  selfElectedStateFundingProgram,
}: {
  handleContinue: () => void;
  setState: (
    stateAbbreviation: string,
    stateName: string,
    activateSearchFilters: boolean,
  ) => void;
  selfElectedStateFundingProgram: SelfElectedStateFundingProgram;
}) {
  const { t } = useTranslation(
    "client\\components\\banners\\SelfIdentificationPublicFunding",
  );
  const [stateMenuOpen, setStateMenuOpen] = React.useState(false);
  const stateMenuAnchorRef = React.useRef<HTMLDivElement>(null);

  return (
    <Box>
      <Typography variant="h4" emphasized>
        {t("Which state is your Learner in?")}
      </Typography>
      <Box
        flex
        sx={{
          flexDirection: "column",
          gap: 20,
          mt: 10,
          p: 20,
          backgroundColor: "primary.50",
          borderRadius: 10,
        }}
      >
        <Typography variant="body1">
          {t(
            "Each state has different options for funding your child’s education.",
          )}
        </Typography>
        <Typography variant="body1" emphasized>
          {t("Select your state to learn more.")}
        </Typography>
        <Box
          ref={stateMenuAnchorRef}
          flex
          sx={{
            cursor: "pointer",
            display: "flex",
            height: 56,
            padding: 12,
            px: 20,
            alignItems: "center",
            gap: 12,
            alignSelf: "stretch",
            borderRadius: 8,
            background: "white",
            boxShadow:
              "0 0 4px 0 rgba(0, 0, 0, 0.099),0 4px 8px 0 rgba(0, 0, 0,0.2)",
            flexDirection: "row",
            justifyContent: "space-between",
            width: "100%",
          }}
          onClick={() => setStateMenuOpen(true)}
        >
          {selfElectedStateFundingProgram.stateName || t("Select your state")}
          <Icon icon={faChevronDown} sx={{ marginLeft: 5 }} />
        </Box>
        <Menu
          anchorEl={stateMenuAnchorRef.current}
          open={stateMenuOpen}
          onClose={() => setStateMenuOpen(false)}
          sx={{ width: 500 }}
        >
          <StateOptions
            width={stateMenuAnchorRef?.current?.clientWidth || 300}
            onStateSelect={(stateAbbreviation: string, stateName: string) => {
              // set the public funding state abbreviation in storage
              setState(stateAbbreviation, stateName, true);
              setStateMenuOpen(false);
            }}
            onClose={() => setStateMenuOpen(false)}
          />
        </Menu>
      </Box>
      <TrackedButton
        variant="contained"
        trackingName="public_funding_self_identification_state_modal_continue"
        onClick={handleContinue}
        disabled={!selfElectedStateFundingProgram.stateAbbreviation}
        sx={{
          width: "100%",
          mt: 20,
        }}
      >
        {t("Show me my options")}
      </TrackedButton>
    </Box>
  );
}

const StateOptions = ({
  onStateSelect,
  onClose,
  width,
}: {
  onStateSelect: (stateAbbreviation: string, stateName: string) => void;
  onClose: () => void;
  width: number;
}) => {
  const fundingStateOptions = US_STATES.filter(({ abbreviation }) =>
    statesHaveFundingProgram.includes(abbreviation),
  );
  return (
    <>
      {fundingStateOptions.map(({ name, abbreviation }, index) => (
        <MenuItem
          key={index}
          onClick={() => {
            onStateSelect(abbreviation, name);
            onClose();
          }}
          sx={{ width }}
        >
          <Typography variant="body1" sx={{ px: 10 }}>
            {name}
          </Typography>
        </MenuItem>
      ))}
    </>
  );
};
